import { Component, signal, OnInit, inject, effect, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IconComponent } from '../../../shared/icons/icon.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { InputComponent } from '../../../shared/components/ui/input/input.component';
import { ToggleComponent } from '../../../shared/components/ui/toggle/toggle.component';
import { ApiService } from '../../../core/services/api.service';
import { AuthService } from '../../../core/services/auth.service';
import { userRoleLabel } from '../../../core/services/user-display';
import { RequestProductsComponent } from '../access/request-products.component';

@Component({
  selector: 'app-user-profile',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IconComponent,
    ButtonComponent,
    InputComponent,
    ToggleComponent,
    RequestProductsComponent
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './profile.component.html'
})
export class UserProfileComponent implements OnInit {
  private apiService = inject(ApiService);
  private authService = inject(AuthService);
  private fb = inject(FormBuilder);

  user = signal<any>(null);
  roleLabel = computed(() => userRoleLabel(this.user()));
  isLoading = signal<boolean>(false);

  // Modal State
  showProfileModal = signal<boolean>(false);
  showPasswordModal = signal<boolean>(false);
  isSubmitting = signal<boolean>(false);

  profileForm!: FormGroup;
  passwordForm!: FormGroup;

  constructor() {
    // Effect to sync AuthService user to local signal if not yet set
    effect(() => {
      const authUser = this.authService.currentUser();
      if (authUser && !this.user()) {
        this.user.set(authUser);
        this.patchFormValues(authUser);
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit() {
    this.initForm();
    this.fetchUserProfile();
  }

  initForm() {
    this.profileForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      isTwoFAEnabled: [false]
    });

    this.passwordForm = this.fb.group({
      currentPassword: ['', Validators.required],
      // The prism-new swagger's rule: 8-72 chars with uppercase, lowercase, a number and a special character
      newPassword: ['', [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(72),
        Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/),
      ]],
      confirmPassword: ['', Validators.required]
    }, { validators: this.passwordMatchValidator });
  }

  passwordMatchValidator(g: FormGroup) {
    return g.get('newPassword')?.value === g.get('confirmPassword')?.value
      ? null : { mismatch: true };
  }

  fetchUserProfile() {
    // 1. Try Local Storage
    const storedProfile = localStorage.getItem('user_profile');
    if (storedProfile) {
      try {
        const user = JSON.parse(storedProfile);
        this.user.set(user);
        this.patchFormValues(user);
        return;
      } catch (e) {
        console.error('Error parsing user profile from local storage', e);
      }
    }

    // 2. Fallback to AuthService (handled by effect as well, but good to have here)
    const authUser = this.authService.currentUser();
    if (authUser) {
      this.user.set(authUser);
      this.patchFormValues(authUser);
    }
  }

  private patchFormValues(user: any) {
    if (!user) return;
    this.profileForm.patchValue({
      firstName: user.firstName || (user.name ? user.name.split(' ')[0] : ''),
      lastName: user.lastName || (user.name ? user.name.split(' ').slice(1).join(' ') : ''),
      isTwoFAEnabled: user.isTwoFactorEnabled || user.isTwoFAEnabled || false
    });
  }

  // Profile Modal Methods
  openProfileModal() {
    // Re-patch value in case it changed or wasn't loaded yet
    if (this.user()) {
      this.profileForm.patchValue({
        firstName: this.user().firstName,
        lastName: this.user().lastName,
        isTwoFAEnabled: this.user().isTwoFactorEnabled || this.user().isTwoFAEnabled || false
      });
    }
    this.showProfileModal.set(true);
  }

  closeProfileModal() {
    this.showProfileModal.set(false);
  }

  onProfileUpdateSubmit() {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);
    const payload = this.profileForm.value;

    this.apiService.patch('/user/profile', payload, {
      showLoader: true,
      showSuccessToaster: true,
      successMessage: 'Profile updated successfully'
    }).subscribe({
      next: (response: any) => {
        this.isSubmitting.set(false);

        // Update local state
        const updatedUser = { ...this.user(), ...payload };
        // Explicitly map properties if API response format is different
        if (response?.data?.user) {
          Object.assign(updatedUser, response.data.user);
        }

        this.user.set(updatedUser);
        localStorage.setItem('user_profile', JSON.stringify(updatedUser));

        //  CRITICAL: Sync with AuthService so other components (Navbar) update immediately
        this.authService.setUser(updatedUser);

        this.closeProfileModal();
      },
      error: () => {
        this.isSubmitting.set(false);
      }
    });

  }


  // Password Modal Methods
  openPasswordModal() {
    this.passwordForm.reset();
    this.showPasswordModal.set(true);
  }

  closePasswordModal() {
    this.showPasswordModal.set(false);
  }

  getError(controlName: string): string {
    const control = this.passwordForm.get(controlName);
    if (control?.touched && control?.errors) {
      if (control.errors['required']) return 'This field is required';
      if (control.errors['minlength']) return 'Password must be at least 8 characters';
      if (control.errors['maxlength']) return 'Password must be at most 72 characters';
      if (control.errors['pattern']) return 'Password must include uppercase and lowercase letters, a number and a special character';
      if (control.errors['mismatch']) return 'Passwords do not match';
    }
    if (controlName === 'confirmPassword' && this.passwordForm.hasError('mismatch') && control?.touched) {
      return 'Passwords do not match';
    }
    return '';
  }

  onChangePasswordSubmit() {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);
    const { currentPassword, newPassword } = this.passwordForm.value;

    this.apiService.post('/auth/change-password', {
      oldPassword: currentPassword,
      newPassword
    }, {
      showLoader: true,
      showSuccessToaster: true,
      successMessage: 'Password changed successfully',
      errorMessage: 'Failed to change password'
    }).subscribe({
      next: () => {
        this.isSubmitting.set(false);
        this.closePasswordModal();
      },
      error: () => {
        this.isSubmitting.set(false);
      }
    });
  }
}
